import { ApiService } from 'react-angle-dashboard-components'
import {
  IAssociateMedicalPlansDTO,
  ICensusDTO,
  ICensusRiskAssessmentDTO,
  ICensusUpdateDTO,
  ICloneQuoteResponseDTO,
  ICreateGroupDTO,
  ICreateQuoteDTO,
  IGetFileDTO,
  IGroupChoiceDTO,
  IGroupDTO,
  IGroupQueryParams,
  IMedicalPlanCreateDTO,
  IMedicalPlanDTO,
  IPastMedicalPlanCreateDTO,
  IPastMedicalPlanDTO,
  IPatchQuoteDTO,
  IQuoteDTO,
  IRateStatus,
  IStateChoicesDTO,
  ISoldQuoteDTO,
  IVendorChoicesDTO,
  ICreateAgentOfRecordDTO,
  ICreateAgencyDTO,
  IGeneralAgencyDTO,
  IAgenciesDTO,
  IOfficesDTO,
  IBrokersDTO
} from './dto'

export class QuoteService extends ApiService {
  createQuote = (data: ICreateQuoteDTO) => {
    return this.request({
      method: 'post',
      url: `/quoting/quotes`,
      credentials: true,
      data
    })
  }

  createAgentOfRecord = (dto: ICreateAgentOfRecordDTO) => {
    const { quoteId, ...data } = dto;
    return this.request({
      method: 'put',
      url: `/quoting/quotes/${quoteId}/agent-of-record`,
      credentials: true,
      data: {
        name: data.agentOfRecordName,
        email: data.agentOfRecordEmailAddress
      }
    })
  }

  createGeneralAgency = async (data: ICreateAgencyDTO) => {
    return await this.request({
      method: 'post',
      url: `/quoting/general-agency`,
      credentials: true,
      data: {
        name: data.generalAgency
      }
    })
  }

  associateGeneralAgency = async (quoteId: number, generalAgencyId: number) => {
    return this.request({
      method: 'put',
      url: `/quoting/quotes/${quoteId}/general-agency`,
      credentials: true,
      data: {
        agency_id: generalAgencyId
      }
    })
  }

  updateQuote = (quoteId: string, data: Partial<IPatchQuoteDTO>) => {
    return this.request({
      method: 'patch',
      url: `/quoting/quotes/${quoteId}`,
      credentials: true,
      data
    })
  }

  associateMedicalPlansToQuote = (
    quoteId: string,
    data: IAssociateMedicalPlansDTO
  ) => {
    return this.request({
      method: 'put',
      url: `/quoting/quotes/${quoteId}/medical-plans`,
      credentials: true,
      data
    })
  }

  getCensusInfo = (quoteId: string) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${quoteId}/census`,
      credentials: true
    }).then((res) => {
      return res as ICensusDTO
    })
  }

  associateCensusToQuote = (quoteId: string, data: ICensusUpdateDTO) => {
    return this.request({
      method: 'put',
      url: `/quoting/quotes/${quoteId}/census`,
      credentials: true,
      data
    })
  }

  triggerCensusRiskAssessment = (quoteId: string) => {
    return this.request({
      method: 'post',
      url: `/quoting/quotes/${quoteId}/census/assess-risk`,
      credentials: true
    })
  }

  getCensusRiskAssessment = (quoteId: string) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${quoteId}/census/assess-risk`,
      credentials: true
    }).then((res: ICensusRiskAssessmentDTO) => {
      return {
        ...res,
        stillProcessing: res.status !== 'completed'
      }
    })
  }

  getQuoteById = async (id: string) => {
    let quote = await this.request({
      method: 'get',
      url: `/quoting/quotes/${id}`,
      credentials: true
    })

    quote = {
      ...quote,
      agentOfRecordName: quote.agentOfRecord?.name,
      agentOfRecordEmailAddress: quote.agentOfRecord?.email,
      generalAgencyId: quote.generalAgency?.id,
      generalAgencyName: quote.generalAgency?.name,
    } as IQuoteDTO

    return quote;
  }

  createGroup = (data: ICreateGroupDTO) => {
    return this.request({
      method: 'post',
      url: `/quoting/groups`,
      credentials: true,
      data
    })
  }

  updateGroup = (groupId: number, data: Partial<ICreateGroupDTO>) => {
    return this.request({
      method: 'patch',
      url: `/quoting/groups/${groupId}`,
      credentials: true,
      data
    })
  }

  getBrokers = () => {
    return this.request({
      method: 'get',
      url: `/quoting/brokers`,
      credentials: true
    }).then((res) => {
      return res as IBrokersDTO[]
    })
  }

  getAgencies = () => {
    return this.request({
      method: 'get',
      url: `/quoting/agencies`,
      credentials: true
    }).then((res) => {
      return res as IAgenciesDTO[]
    })
  }

  getOffices = () => {
    return this.request({
      method: 'get',
      url: `/quoting/offices`,
      credentials: true
    }).then((res) => {
      return res as IOfficesDTO[]
    })
  }

  getGroups = (params?: IGroupQueryParams) => {
    if (params) {
      let url = `/quoting/groups?page_number=${params.page_number}&page_limit=${params.page_limit}`

      if (params.ordering) {
        if (params.ordering.direction === 'asc') {
          url += `&ordering=${params.ordering.column}`
        } else if (params.ordering.direction === 'desc') {
          url += `&ordering=-${params.ordering.column}`
        }
      }

      if (params.groupName) {
        url += `&groupName[icontains]=${params.groupName}`
      }

      if (params.email) {
        url += `&email[icontains]=${params.email}`
      }

      if (params.state && params.state !== 'All') {
        url += `&state=${params.state}`
      }

      if (params.effectiveDateGte) {
        url += `&effectiveDate[gte]=${params.effectiveDateGte
          .toISOString()
          .substring(0, 10)}`
      }

      if (params.effectiveDateLte) {
        url += `&effectiveDate[lte]=${params.effectiveDateLte
          .toISOString()
          .substring(0, 10)}`
      }

      if (params.lastQuoteDateGte) {
        url += `&lastQuoteDate[gte]=${params.lastQuoteDateGte
          .toISOString()
          .substring(0, 10)}`
      }

      if (params.lastQuoteDateLte) {
        url += `&lastQuoteDate[lte]=${params.lastQuoteDateLte
          .toISOString()
          .substring(0, 10)}`
      }

      if (params.lastQuoteStatus) {
        url += `&lastQuoteStatus=${params.lastQuoteStatus}`
      }

      return this.request({
        method: 'get',
        url: url,
        credentials: true
      }).then(
        (res) =>
          res as {
            data: IGroupDTO[]
            count: number
            totalCount: number
            pageLimit: number
            pageNumber: number
            numPages: number
            tableType?: string
          }
      )
    } else {
      return this.request({
        method: 'get',
        url: `/quoting/groups`,
        credentials: true
      }).then((res) => res as { data: IGroupDTO[], tableType?: string })
    }
  }

  getGroup = (groupId: number) => {
    let url = `/quoting/groups/${groupId}`

    return this.request({
      method: 'get',
      url: url,
      credentials: true
    }).then((response) => response as IGroupDTO)
  }

  getGroupChoices = () => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/group-choices`,
      credentials: true
    }).then((res) => {
      return res.data as IGroupChoiceDTO[]
    })
  }

  getGeneralAgencies = (searchTerm: string): Promise<IGeneralAgencyDTO[]> => {
    return this.request({
      method: 'get',
      url: `/quoting/general-agency`,
      params: {
        name: searchTerm,
      },
      credentials: true
    })
  }

  getVendorChoices = (id: number) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${id}/program-choices`,
      credentials: true
    }).then((res) => res as IVendorChoicesDTO)
  }

  getMedicalPlans = (id: number) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${id}/medical-plans`,
      credentials: true
    }).then((res) => res as IMedicalPlanDTO[])
  }

  createMedicalPlan = (data: IMedicalPlanCreateDTO) => {
    return this.request({
      method: 'post',
      url: `/quoting/medical-plans`,
      credentials: true,
      data
    }).then((res) => res as IMedicalPlanDTO)
  }

  editMedicalPlan = (
    entryId: string | number,
    data: Partial<IMedicalPlanCreateDTO>
  ) => {
    return this.request({
      method: 'patch',
      url: `/quoting/medical-plans/${entryId}`,
      credentials: true,
      data
    }).then((res) => res as IMedicalPlanDTO)
  }

  deleteMedicalPlan = (entryId: string | number) => {
    return this.request({
      method: 'delete',
      url: `/quoting/medical-plans/${entryId}`,
      credentials: true
    })
  }

  getPastMedicalPlans = (id: string) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${id}/past-medical-plans`,
      credentials: true
    }).then((res) => res.data as IPastMedicalPlanDTO[])
  }

  createPastMedicalPlan = (id: string, data: IPastMedicalPlanCreateDTO) => {
    return this.request({
      method: 'post',
      url: `/quoting/quotes/${id}/past-medical-plans`,
      credentials: true,
      data
    }).then((res) => res as IPastMedicalPlanDTO)
  }

  updatePastMedicalPlan = (
    quoteId: string,
    entryId: string,
    data: IPastMedicalPlanCreateDTO
  ) => {
    return this.request({
      method: 'patch',
      url: `/quoting/quotes/${quoteId}/past-medical-plans/${entryId}`,
      credentials: true,
      data
    })
  }

  deletePastMedicalPlan = (quoteId: string, entryId: string) => {
    return this.request({
      method: 'delete',
      url: `/quoting/quotes/${quoteId}/past-medical-plans/${entryId}`,
      credentials: true
    })
  }

  getExperienceFiles = (quoteId: string) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${quoteId}/files?fileType=experience_info`,
      credentials: true
    }).then((res) => res.data as IGetFileDTO[])
  }

  getMonthlyInvoiceFiles = (quoteId: string) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${quoteId}/files?fileType=monthly_invoice`,
      credentials: true
    }).then((res) => res.data as IGetFileDTO[])
  }

  uploadFile = (quoteId: number, data: FormData) => {
    return this.request({
      method: 'post',
      url: `/quoting/quotes/${quoteId}/files`,
      headers: {
        'Content-Type':
          'multipart/form-data; boundary=20639c8395c2a499779e9ec7758bbbe6'
      },
      credentials: true,
      data
    })
  }

  deleteUploadedFile = (quoteId: string | number, fileId: string | number) => {
    return this.request({
      method: 'delete',
      url: `/quoting/quotes/${quoteId}/files/${fileId}`,
      credentials: true
    })
  }

  markQuoteAsSold = (quoteId: string, data: ISoldQuoteDTO) => {
    return this.request({
      method: 'post',
      url: `/quoting/quotes/${quoteId}/mark-sold`,
      credentials: true,
      data
    })
  }

  triggerRateCalculation = (quoteId: string) => {
    return this.request({
      method: 'get',
      url: `/quoting/quotes/${quoteId}/rates-status`,
      credentials: true
    }).then((res) => res as { status: IRateStatus })
  }

  downloadExcelProposal = (quoteId: string) => {
    return this.request({
      method: 'get',
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      responseType: 'arraybuffer',
      url: `/quoting/quotes/${quoteId}/proposal-xlsx`,
      credentials: true
    }).then((res) => new Blob([res]))
  }

  cloneQuoteToDraft = (quoteId: string) => {
    return this.request({
      method: 'post',
      url: `/quoting/quotes/${quoteId}/clone`,
      credentials: true
    }).then((res) => res as ICloneQuoteResponseDTO)
  }

  getStateChoices = async () => {
    return this.request({
      method: 'get',
      url: 'quoting/quotes/supported-states',
      credentials: true
    }).then((res) => res as IStateChoicesDTO)
  }
}
